import React, { useState, useEffect, useRef } from "react";
import {
  FaBus,
  FaWalking,
  FaCalendarAlt,
  FaExchangeAlt,
  FaMapMarkerAlt,
} from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import BusTimetable from "./BusTimetable";
import Features from "./features"; 
import { useLocation } from "react-router-dom";

import "./SearchByRoute.css";
import {
  searchByRouteAPI,
  searchByStopAPI,
  getStopsAPI,
} from "../api";

export default function SearchByRoute() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [stop, setStop] = useState("");
  const [selectedView, setSelectedView] = useState("route");
  const [results, setResults] = useState([]);
  const [showImage, setShowImage] = useState(true);
  const [selectedBus, setSelectedBus] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [errors, setErrors] = useState({ from: "", to: "", stop: "" });
  const [error, setError] = useState("");

  const [allStops, setAllStops] = useState([]);
  const [fromSuggestions, setFromSuggestions] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);
  const [stopSuggestions, setStopSuggestions] = useState([]);
  const [focusedInput, setFocusedInput] = useState(null);

  const containerRef = useRef(null);

  const [searchedFrom, setSearchedFrom] = useState("");
const [searchedTo, setSearchedTo] = useState("");
const [searchedStop, setSearchedStop] = useState("");

  const swapFromTo = () => {
    setFrom(to);
    setTo(from);
  };

const formatDate = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

 
const formatTimeTo12Hour = (time) => {
  if (!time) return "";
  const [hours, minutes] = time.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, minutes);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};
const calculateDuration = (start, end) => {
    if (!start || !end) return "";
    const startTime = new Date(`1970-01-01T${start}`);
    const endTime = new Date(`1970-01-01T${end}`);
    let diff = (endTime - startTime) / (1000 * 60);
    if (diff < 0) diff += 24 * 60;
    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;
    return `${hours}h ${minutes}m`;
  };

 
  const isNoBusError = (err) => {
    const status = err?.response?.status;
    const text =
      (err?.response?.data &&
        JSON.stringify(err.response.data).toLowerCase()) ||
      "";
    return (
      status === 404 ||
      status === 204 ||
      text.includes("no bus") ||
      text.includes("no result")
    );
  };

 
useEffect(() => {
  const fetchStops = async () => {
    try {
      const res = await getStopsAPI();
      const stopsList = res.data.map((stop) => stop.name);
      setAllStops(stopsList);
    } catch (err) {
      console.error("Error fetching stops:", err);
    }
  };

  fetchStops();
}, []);


  
  const filterSuggestions = (value) => {
    if (!value) return [];
    const lower = value.toLowerCase();
    const startsWith = allStops.filter((s) =>
      s.toLowerCase().startsWith(lower)
    );
    const includes = allStops.filter(
      (s) => s.toLowerCase().includes(lower) && !s.toLowerCase().startsWith(lower)
    );
    return [...startsWith, ...includes].slice(0, 10);
  };


const sortByTime = (a, b) => {
  const getMinutes = (time) => {
    if (!time) return Infinity; 
    const [hours, minutes] = time.split(":").map(Number);
    return hours * 60 + minutes;
  };

  
  const timeA = selectedView === "route" ? getMinutes(a.departure_time) : getMinutes(a.stop_time);
  const timeB = selectedView === "route" ? getMinutes(b.departure_time) : getMinutes(b.stop_time);

  return timeA - timeB;
};

  useEffect(() => {
    if (selectedView === "route") {
      setFromSuggestions(filterSuggestions(from));
      setToSuggestions(filterSuggestions(to));
    
    } else {
      setStopSuggestions(filterSuggestions(stop));
   
    }
  }, [from, to, stop, allStops, selectedView]);

  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setFromSuggestions([]);
        setToSuggestions([]);
        setStopSuggestions([]);
        setFocusedInput(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

 
  const handleSelectSuggestion = (value, type) => {
    if (type === "from") setFrom(value);
    if (type === "to") setTo(value);
    if (type === "stop") setStop(value);

   
    setFromSuggestions([]);
    setToSuggestions([]);
    setStopSuggestions([]);
    setFocusedInput(null);
  };

 
  const doSearch = async (e) => {
  if (e) e.preventDefault();
  setErrors({ from: "", to: "", stop: "" });
  setError("");
  setResults([]);

  try {
    const dateParam = selectedDate ? formatDate(selectedDate) : null;

   

    let response, data;

    if (selectedView === "route") {
      // validate inputs
      if (!from.trim() || !to.trim()) {
        setErrors({
          from: !from.trim() ? "Please enter your Origin City" : "",
          to: !to.trim() ? "Please enter your Destination City" : "",
        });
        return;
      }

      setSearchedFrom(from);
      setSearchedTo(to);
      setShowImage(false);

      
      response = await searchByRouteAPI(from, to, dateParam);
      data = response.data;

    } else {
     
      if (!stop.trim()) {
        setErrors({ stop: "Please enter your Place" });
        return;
      }

      setSearchedStop(stop);
      setShowImage(false);

      
      response = await searchByStopAPI(stop, dateParam);
      data = response.data;

      
      if (data.results) {
        data.results = data.results.filter(
          (bus) => stop.toLowerCase() !== bus.end_stop?.name?.toLowerCase()
        );
      }
    }

    
    if (!data || data.count === 0 || !data.results?.length) {
      setError(
        selectedView === "route"
          ? "No buses available on this route."
          : "No buses available for this stop."
      );
      return;
    }

  

    if (data.results.length === 0) {
      setError(
        selectedView === "route"
          ? "No buses available on this route."
          : "No buses available for this stop."
      );
      return;
    }

    
    setResults(data.results.sort(sortByTime));

  } catch (err) {
    console.error(err);
    if (isNoBusError(err)) {
      setError(
        selectedView === "route"
          ? "No bus services currently run on this route."
          : "No bus services available for this stop."
      );
      setShowImage(false);
    } else {
      setError("Something went wrong. Please try again.");
    }
  }

  window.scrollTo({ top: 0, behavior: "smooth" });
};

  
  const handleToggleView = (view) => {
    setSelectedView(view);
    setResults([]);
    setError("");
    setShowImage(true);
  };

  
  const renderAvailableDays = (availableDays, timetable) => {
    if (availableDays) {
      const days = Object.entries(availableDays);
      const allActive = days.every(([_, active]) => active);
      if (allActive) return <span className="runs-daily">Runs Daily</span>;
      return days.map(([day, isActive]) => (
        <span key={day} className={`day ${isActive ? "active" : "inactive"}`}>
          {day.slice(0, 3).toUpperCase()}
        </span>
      ));
    }

    if (timetable) {
      const daysMap = {
        mon: "Mon",
        tue: "Tue",
        wed: "Wed",
        thu: "Thu",
        fri: "Fri",
        sat: "Sat",
        sun: "Sun",
      };
      const activeDays = Object.entries(daysMap).filter(
        ([key]) => timetable[key]
      );
      if (activeDays.length === 7)
        return <span className="runs-daily">Runs Daily</span>;
      return Object.entries(daysMap).map(([key, label]) => (
        <span
          key={key}
          className={`day ${timetable[key] ? "active" : "inactive"}`}
        >
          {label}
        </span>
      ));
    }

    return null;
  };
  const location = useLocation();

useEffect(() => {
  if (location.pathname === "/") {
    setShowImage(true);
    setResults([]);
    setError("");
    setSelectedBus(null);
  }
}, [location.pathname, location.key]);



  return (
    <div className="bus-container" ref={containerRef}>
      {showImage && (
        <div className="bus-image-container">
          <img src="/background_bus1.jpg" alt="Bus" className="bus-image" />
        </div>
      )}

      <div className={`form-wrapper ${showImage ? "overlay" : "fixed-top"}`}>
        <div className="overlay-buttons">
          <button
            type="button"
            className={`toggle-btn ${selectedView === "route" ? "active" : ""}`}
            onClick={() => handleToggleView("route")}
          >
            Search by Route
          </button>
          <button
            type="button"
            className={`toggle-btn ${selectedView === "stop" ? "active" : ""}`}
            onClick={() => handleToggleView("stop")}
          >
            Search by Stop
          </button>
        </div>

        <div
          className={`form-card ${
            selectedView === "route" ? "route-view" : "stop-view"
          }`}
        >
          {selectedView === "route" ? (
            <div className="form-horizontal">
              
              <div className="field-wrapper" style={{ position: "relative" }}>
                <div className="field horizontal-field">
                  <div className="icon-group">
                    <FaWalking className="icon" />
                    <FaBus className="icon" />
                  </div>
                  <input
                    type="text"
                    placeholder="Journey Start"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                    onFocus={() => setFocusedInput("from")}
                  />
                </div>
                {focusedInput === "from" && fromSuggestions.length > 0 && (
                  <ul className="suggestions-list">
                    {fromSuggestions.map((s, idx) => (
                      <li
                        key={idx}
                        onMouseDown={() => handleSelectSuggestion(s, "from")}
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                )}
                {errors.from && <p className="error-message">{errors.from}</p>}
              </div>

              
              <div className="divider-with-arrow" onClick={swapFromTo}>
                <div className="divider-line"></div>
                <FaExchangeAlt className="double-arrow-icon" />
              </div>

              
              <div className="field-wrapper" style={{ position: "relative" }}>
                <div className="field horizontal-field">
                  <div className="icon-group">
                    <FaBus className="icon" />
                    <FaWalking className="icon" />
                  </div>
                  <input
                    type="text"
                    placeholder="Journey End"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    onFocus={() => setFocusedInput("to")}
                  />
                </div>
                {focusedInput === "to" && toSuggestions.length > 0 && (
                  <ul className="suggestions-list">
                    {toSuggestions.map((s, idx) => (
                      <li
                        key={idx}
                        onMouseDown={() => handleSelectSuggestion(s, "to")}
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                )}
                {errors.to && <p className="error-message">{errors.to}</p>}
              </div>

              {/* Date */}
              <div className="field horizontal-field date-field">
                <FaCalendarAlt className="calendar-icon" />
                <div style={{ flex: 1 }}>
                  <DatePicker
                    selected={selectedDate}
                    onChange={(date) => setSelectedDate(date)}
                    minDate={new Date()}
                    dateFormat="dd MMM yyyy"
                    placeholderText="Journey Date"
                    popperPlacement="bottom-start"
                    className="date-input"
                  />
                </div>
              </div>

              {!showImage && (
                <div className="search-btn-wrapper inside-card">
                  <button onClick={doSearch} className="search-btn">
                    Search Bus
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="form-horizontal" style={{ position: "relative" }}>
              <div className="input-wrapper">
                <FaMapMarkerAlt className="input-icon" />
                <input
                  type="text"
                  placeholder="Enter Stop"
                  value={stop}
                  onChange={(e) => setStop(e.target.value)}
                  onFocus={() => setFocusedInput("stop")}
                />
              </div>
              {focusedInput === "stop" && stopSuggestions.length > 0 && (
                <ul className="suggestions-list">
                  {stopSuggestions.map((s, idx) => (
                    <li
                      key={idx}
                      onMouseDown={() => handleSelectSuggestion(s, "stop")}
                    >
                      {s}
                    </li>
                  ))}
                </ul>
              )}
              {errors.stop && <p className="error-message">{errors.stop}</p>}

              {!showImage && (
                <div className="search-btn-wrapper inside-card">
                  <button onClick={doSearch} className="search-btn">
                    Search Bus
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showImage && (
        <div className="search-btn-wrapper outside-card">
          <button onClick={doSearch} className="search-btn">
            Search Bus
          </button>
          <div className="demo-notice-wrapper">
            <p className="demo-notice-text">
           🚍 Evide Bus is now live. We're integrating reliable, real-time route and timing 
           data from private bus operators — full route information will be available soon!
            </p>
          </div>
        </div>
      )}
     {showImage && <Features />}

      {/* Results OR Error */}
      {!showImage && (
        <div className="bus-list">
         {error && (
  <div className="no-bus">
    <img
      src="/nobus.svg"
      alt="No buses available"
      className="no-bus-img"
    />
    <p className="no-bus-subtext">
      We are live and working to bring you real-time private bus
      data. Timings for this route/stop are not available yet as
      integration with private operators is in progress.
    </p>
  </div>
)}

          {!error &&
            results.length > 0 &&
            results.map((bus) => (
              <div
                key={bus.trip_id || bus.id}
                className="bus-card"
                onClick={() => setSelectedBus(bus)}
              >
                <div className="bus-card-header">
                  <div className="bus-info">
                    <h3 className="bus-name">{bus.bus?.name}</h3>
                    <p className="bus-number">{bus.bus?.number}</p>
                  </div>
                  <div className="active-days">
                    {renderAvailableDays(bus.available_days, bus.timetable)}
                  </div>
                </div>

                <div className="bus-route">
                  {selectedView === "route" ? (
                    <>
                     <p className="route">{`${searchedFrom} → ${searchedTo}`}</p>
                     <p className="time">
                      {formatTimeTo12Hour(bus.departure_time)} <span className="dots"></span>{" "}
                      <span className="duration">
                        {calculateDuration(bus.departure_time, bus.arrival_time)}
                        </span>
                        <span className="dots"></span>
                        {formatTimeTo12Hour(bus.arrival_time)}
                        </p>
                        </>
                         ) : (
                         <>
                         <p className="route">{`${searchedStop} → ${bus.end_stop.name}`}</p>
                         <p className="time">
                         {formatTimeTo12Hour(bus.stop_time)} <span className="dots"></span>{" "}
                         <span className="duration">
                          {calculateDuration(bus.stop_time, bus.end_stop_time)}
                         </span>
                         <span className="dots"></span>
                         {formatTimeTo12Hour(bus.end_stop_time)}
                         </p>
                         </>
                  )}
                </div>
              </div>
            ))}
        </div>
      )}
       {selectedBus && (
        <BusTimetable
          bus={selectedBus}
          selectedDate={formatDate(selectedDate)}
          onClose={() => setSelectedBus(null)}
        />
      )}
    </div>
  );
}